<template>
  <div class="tl mb">
    <Breadcrumbs main="Трансляция MLBB" title="Эфир MLBB"/>

    <div class="container-fluid tl-container mb-stack">
      <!-- трансляция = один эфир/OBS: сцена, игра и правки графики живут в ней -->
      <section class="mb-panel mb-cast">
        <span class="mb-muted mb-small">Трансляция</span>
        <select v-if="broadcasts.length"
                class="mb-select mb-cast__select"
                :value="broadcastId || ''"
                aria-label="Трансляция"
                @change="selectBroadcast(Number($event.target.value) || null)">
          <option v-if="!broadcastId" value="" disabled>Выберите трансляцию</option>
          <option v-for="b in broadcasts" :key="b.id" :value="b.id">{{ broadcastLabel(b) }}</option>
        </select>
        <span v-else-if="broadcastsLoading" class="mb-loading">Загружаем</span>
        <span v-else class="mb-muted mb-small">ещё ни одной — создайте, чтобы выводить графику в OBS</span>
        <span v-if="broadcastId && broadcastMissing" class="mb-error-text">трансляция #{{ broadcastId }} не найдена</span>
        <span class="mb-spacer"></span>
        <button type="button"
                class="mb-btn mb-btn--ghost mb-btn--sm"
                :disabled="creatingBroadcast"
                @click="createBroadcast">
          <i class="fa fa-plus"></i> Новая трансляция
        </button>
      </section>

      <!-- компактное состояние подключения: судья + источник -->
      <section v-if="sourceBattleId" class="mb-panel mb-source">
        <div class="mb-source__main">
          <span class="mb-muted mb-small">Судья</span>
          <b class="mb-mono">{{ judgeId || '—' }}</b>
          <span class="mb-source__sep">|</span>
          <span class="mb-muted mb-small">Источник</span>
          <template v-if="battle">
            <b class="mb-ellipsis mb-source__title" :title="battleTitle(battle)">{{ battleTitle(battle) }}</b>
            <span :class="['mb-badge', 'mb-badge--' + stateVariant(battle.state)]">{{ stateLabel(battle.state) }}</span>
            <span v-if="battle.state === 'play'" class="mb-mono">{{ formatGameTime(battle.game_time) }}</span>
            <span v-if="battle.group_id" class="mb-badge mb-badge--success">матч #{{ battle.group_id }}</span>
          </template>
          <span v-else-if="liveLoadError" class="mb-error-text">не удалось загрузить</span>
          <span v-else class="mb-loading">Загружаем</span>
          <span class="mb-spacer"></span>
          <button type="button" class="mb-btn mb-btn--ghost mb-btn--sm" @click="changeMatch">Сменить матч</button>
        </div>

        <div v-if="series.length > 1 || newerGame" class="mb-source__series">
          <span class="mb-muted mb-small">Игры серии:</span>
          <button v-for="(g, i) in series"
                  :key="g.id"
                  type="button"
                  :class="['mb-chip', {'is-active': g.id === sourceBattleId}]"
                  :disabled="g.id === sourceBattleId"
                  @click="selectSource(g)">
            Игра {{ i + 1 }} · {{ stateLabel(g.state) }}
          </button>
          <span v-if="newerGame" class="mb-alert">
            Началась следующая игра серии
            <button type="button" class="mb-btn mb-btn--sm" @click="selectSource(newerGame)">Сделать источником</button>
          </span>
        </div>

        <div v-if="battle && !battle.group_id" class="mb-source__unlinked mb-alert">
          Игра не привязана к матчу BIGPLAY — показываем игровые названия команд из Moonton.
          <button type="button" class="mb-btn mb-btn--warn mb-btn--sm" @click="openLink(battle)">Привязать к матчу</button>
        </div>
      </section>

      <MbMoontonStatus v-if="sourceBattleId"
                       :battle="battle"
                       :ws-status="wsStatus"
                       :load-error="liveLoadError"
                       :busy="reconnecting"
                       :now="now"
                       @reconnect="reconnect"/>

      <section class="mb-panel">
        <nav class="mb-tabs" role="tablist">
          <button v-for="t in tabs"
                  :key="t.key"
                  type="button"
                  role="tab"
                  :aria-selected="tab === t.key"
                  :class="['mb-tab', {'is-active': tab === t.key}]"
                  @click="setTab(t.key)">{{ t.title }}</button>
        </nav>

        <div v-show="tab === 'air'" class="mb-panel__body mb-stack">
          <!-- матчи и графика на одном экране: нашёл игру судьи → сразу готовишь и выводишь графику -->
          <div v-if="sourceBattleId" class="mb-matches-toggle">
            <button type="button" class="mb-link" :aria-expanded="String(matchesOpen)" @click="matchesOpen = !matchesOpen">
              <i :class="['fa', matchesOpen ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
              {{ matchesOpen ? 'Скрыть матчи судьи' : 'Матчи судьи' }}
            </button>
          </div>
          <div v-show="matchesOpen || !sourceBattleId">
            <MbMatchesByJudge ref="matches"
                              :judge-id="judgeId"
                              :source-battle-id="sourceBattleId"
                              :judges="judges"
                              :now="now"
                              :reload-key="matchesReloadKey"
                              @search="setJudge"
                              @select="selectSource"
                              @link="openLink"
                              @changed="onBattleChanged"
                              @judges-changed="loadJudges"
                              @battle-updated="onJudgeBattle"/>
          </div>

          <div v-if="!broadcastId" class="mb-alert mb-alert--info">
            Трансляция не выбрана — графику можно готовить, но в OBS она не уйдёт. Выберите или создайте трансляцию сверху.
          </div>
          <div v-if="!sourceBattleId" class="mb-alert mb-alert--info">
            Матч не выбран — доступны только «Без графики» и «Заставка». Найдите игру судьи выше и нажмите «Сделать источником».
          </div>
          <div v-else-if="airElsewhere" class="mb-alert">
            В OBS сейчас выведена другая игра (#{{ airElsewhere }}). Выберите сцену и нажмите «В эфир», чтобы вывести эту.
          </div>
          <MbAirPanel :battle="battle"
                      :preview-battle="previewBattle"
                      :program-battle="programBattle"
                      :events="events"
                      :series="series"
                      :post-battles="previewPostBattles"
                      :program-post-battle="programPostBattle"
                      :preview-title="overrides.title || ''"
                      :preview-casters="overrides.casters || []"
                      :program-title="(broadcast && broadcast.title) || ''"
                      :program-casters="(broadcast && broadcast.casters) || []"
                      :preview="air.preview"
                      :program="air.program"
                      :broadcast-id="broadcastId"
                      :taking="taking"
                      :now="now"
                      :active="tab === 'air'"
                      @preview="patchPreview"
                      @take="take"/>
        </div>

        <div v-if="tab === 'design'" class="mb-panel__body">
          <MbOverridesTab :broadcast="broadcast"
                          :battle="battle"
                          :saving="savingOverrides"
                          @save="saveOverrides"/>
        </div>

        <div v-if="tab === 'data'" class="mb-panel__body">
          <MbDataTab :battle="battle" :events="events" :timeline="timeline" :series="series" :now="now"/>
        </div>
      </section>
    </div>

    <MbLinkMatchModal v-model="linkVisible" :battle="linkBattle" @linked="onLinked"/>
  </div>
</template>

<script>
import api from '@/api/api'
import MbAirPanel from '@/components/MlbbBroadcast/MbAirPanel.vue'
import MbDataTab from '@/components/MlbbBroadcast/MbDataTab.vue'
import MbLinkMatchModal from '@/components/MlbbBroadcast/MbLinkMatchModal.vue'
import MbMatchesByJudge from '@/components/MlbbBroadcast/MbMatchesByJudge.vue'
import MbMoontonStatus from '@/components/MlbbBroadcast/MbMoontonStatus.vue'
import MbOverridesTab from '@/components/MlbbBroadcast/MbOverridesTab.vue'
import {createBattleLive} from '@/services/mlbbBattleLive'
import {createBroadcastLive, programOf, programPayload} from '@/services/mlbbBroadcastLive'
import {loadHeroCatalog} from '@/services/mlbbHeroCatalog'
import {loadGameCatalog} from '@/services/mlbbGameCatalog'
import {reconnectWs} from '@/services/websocket'
import {sceneTitle} from '@/common/mlbbOverlay'
import {applyOverrides} from '@/common/mlbbBroadcastOverrides'
import {
  battleTitle,
  formatGameTime,
  parseJudgeId,
  pushRecentJudge,
  stateLabel,
  stateVariant,
} from '@/common/mlbbBroadcast'

const TABS = [
  {key: 'air', title: 'Матчи и графика'},
  {key: 'design', title: 'Оформление'},
  {key: 'data', title: 'Данные MLBB'},
]
// старые ссылки: «Подготовка графики» и «Матчи по ID» теперь одна вкладка
const LEGACY_TABS = {prep: 'air', matches: 'air'}
// «сек назад» и «данные задерживаются» пересчитываем по таймеру, а не по приходу данных
const CLOCK_MS = 5000
// последняя выбранная трансляция — чтобы после перезагрузки режиссёр попал в свой эфир
export const BROADCAST_STORAGE_KEY = 'mlbb_broadcast_id'

function readStoredBroadcast() {
  try {
    return parseJudgeId(window.localStorage.getItem(BROADCAST_STORAGE_KEY))
  } catch (e) {
    return null
  }
}

export default {
  name: 'MlbbBroadcastPage',
  components: {MbAirPanel, MbDataTab, MbLinkMatchModal, MbMatchesByJudge, MbMoontonStatus, MbOverridesTab},
  data() {
    return {
      tabs: TABS,
      tab: 'air',
      matchesOpen: true,
      judgeId: null,
      judges: [],
      sourceBattleId: null,
      battle: null,
      events: [],
      timeline: [],
      series: [],
      wsStatus: 'closed',
      liveLoadError: '',
      reconnecting: false,
      linkVisible: false,
      linkBattle: null,
      matchesReloadKey: 0,
      now: Date.now(),
      live: null,
      clock: null,
      // трансляция: program — то, что в эфире OBS, уходит на бэк по «В эфир» и приходит обратно по WS
      broadcasts: [],
      broadcastsLoading: false,
      creatingBroadcast: false,
      broadcastId: null,
      broadcast: null,
      broadcastMissing: false,
      broadcastLive: null,
      savingOverrides: false,
      airElsewhere: null,
      taking: false,
      air: {
        preview: {scene: 'none', postId: null},
        program: {scene: 'none', postId: null},
      },
      // завершённые игры серии для «Итогов», догруженные по REST
      postBattleCache: {},
    }
  },
  computed: {
    overrides() {
      return this.broadcast?.overrides || {}
    },
    // превью — источник с сохранёнными правками, как его нарисует OBS после «В эфир»
    previewBattle() {
      return applyOverrides(this.battle, this.overrides)
    },
    previewPostBattles() {
      const map = {}
      Object.entries(this.postBattles).forEach(([id, b]) => {
        map[id] = applyOverrides(b, this.overrides)
      })
      return map
    },
    // эфир — ровно то, что бэк отдаёт OBS (display уже посчитан)
    programBattle() {
      const b = this.broadcast?.battle
      if (b && b.id === this.sourceBattleId) return b
      return this.air.program.scene === 'none' ? null : this.previewBattle
    },
    programPostBattle() {
      const b = this.broadcast?.post_battle
      if (b && b.id === this.air.program.postId) return b
      return this.previewPostBattles[this.air.program.postId] || null
    },
    postBattles() {
      const map = {...this.postBattleCache}
      if (this.battle) map[this.battle.id] = this.battle
      return map
    },
    // игра серии, которая началась после текущего источника
    newerGame() {
      const idx = this.series.findIndex((g) => g.id === this.sourceBattleId)
      if (idx < 0) return null
      return this.series.slice(idx + 1).reverse().find((g) => g.state !== 'end') || null
    },
  },
  watch: {
    broadcastId: 'startBroadcast',
    'air.preview.postId': 'loadPostBattle',
    'air.program.postId': 'loadPostBattle',
    // открыли по ссылке ?battle= без судьи — подставляем судью игры, чтобы работал его WS-канал
    'battle.judge_id'(id) {
      if (id && !this.judgeId) this.setJudge(Number(id))
    },
    'battle.group_id'(groupId, prev) {
      if (groupId !== prev) this.loadSeries()
    },
    'battle.state'(state, prev) {
      // игра закончилась — следующая появится в списке серии
      if (state === 'end' && prev && prev !== 'end') this.loadSeries()
    },
  },
  created() {
    const q = this.$route.query
    this.judgeId = parseJudgeId(q.judge)
    const battleId = parseJudgeId(q.battle)
    this.broadcastId = parseJudgeId(q.broadcast) || readStoredBroadcast()
    const tab = LEGACY_TABS[q.tab] || q.tab
    this.tab = TABS.some((t) => t.key === tab) ? tab : 'air'
    if (battleId) {
      this.startLive(battleId)
      this.matchesOpen = false
    }
    this.loadJudges()
    this.loadBroadcasts()
    loadHeroCatalog()
    loadGameCatalog()
  },
  mounted() {
    this.clock = setInterval(() => {
      this.now = Date.now()
    }, CLOCK_MS)
  },
  beforeDestroy() {
    clearInterval(this.clock)
    this.stopLive()
    this.stopBroadcast()
  },
  methods: {
    battleTitle,
    formatGameTime,
    stateLabel,
    stateVariant,

    syncQuery() {
      const query = {...this.$route.query}
      const set = (key, value) => {
        if (value) query[key] = String(value)
        else delete query[key]
      }
      set('broadcast', this.broadcastId)
      set('judge', this.judgeId)
      set('battle', this.sourceBattleId)
      set('tab', this.tab)
      const same = ['broadcast', 'judge', 'battle', 'tab'].every((k) => (this.$route.query[k] || undefined) === query[k])
      if (!same) this.$router.replace({query}).catch(() => {})
    },

    setTab(key) {
      this.tab = key
      this.syncQuery()
    },

    changeMatch() {
      this.setTab('air')
      this.matchesOpen = true
      this.$nextTick(() => this.$refs.matches && this.$refs.matches.focusInput())
    },

    setJudge(id) {
      this.judgeId = id
      pushRecentJudge(id)
      this.syncQuery()
    },

    async loadJudges() {
      try {
        const res = await api.mlbbEsports.judges({skipErrorToast: true})
        this.judges = res?.data?.data || []
      } catch (e) {
        this.judges = []
      }
    },

    /* ---------- источник данных ---------- */

    async selectSource(b) {
      if (!b || b.id === this.sourceBattleId) return
      if (this.air.program.scene !== 'none' && !(await this.confirmSourceChange(b))) return
      if (this.judgeId) pushRecentJudge(this.judgeId)
      // список уже отдал полный объект — показываем сразу, live догрузит остальное
      this.startLive(b.id, b)
      this.matchesOpen = false
      this.syncQuery()
      // в эфире остаётся та же сцена, но уже по данным новой игры
      if (this.air.program.scene !== 'none') await this.pushAir(this.air.program, b)
    },

    startLive(battleId, initial = null) {
      this.stopLive()
      // завершённая игра могла остаться в «Итогах» — сохраняем её, раз live-источник меняется
      if (this.battle?.state === 'end') this.$set(this.postBattleCache, this.battle.id, this.battle)
      this.sourceBattleId = battleId
      this.battle = initial && initial.id === battleId ? initial : null
      this.events = []
      this.timeline = []
      this.series = []
      this.liveLoadError = ''
      this.live = createBattleLive({
        battleId,
        onBattle: (b) => {
          this.battle = b
          this.liveLoadError = ''
        },
        onEvents: (all) => {
          this.events = all
        },
        onTimeline: (points) => {
          this.timeline = points
        },
        onStatus: (status) => {
          this.wsStatus = status
        },
        onError: (e) => {
          this.liveLoadError = e?.response?.status === 404
              ? 'Игра не найдена'
              : (e?.response?.data?.message || e?.message || 'Ошибка загрузки')
        },
      })
      this.live.start().then(() => this.loadSeries())
    },

    confirmSourceChange(b) {
      return this.$bvModal.msgBoxConfirm(
          `Сейчас в эфире «${sceneTitle(this.air.program.scene)}». Графика сразу переключится на данные игры «${battleTitle(b)}».`,
          {
            title: 'Сменить источник во время эфира?',
            okTitle: 'Сменить источник',
            cancelTitle: 'Отмена',
            okVariant: 'danger',
            centered: true,
          },
      ).catch(() => false)
    },

    stopLive() {
      if (this.live) this.live.stop()
      this.live = null
    },

    // батл судьи из WS-канала mlbb-judges.{id}.battles: обновляем игру серии на месте,
    // новую игру серии (или привязку/отвязку) — перечитываем списком, чтобы порядок был как у бэка
    onJudgeBattle(b) {
      const groupId = this.battle?.group_id
      const idx = this.series.findIndex((g) => g.id === b.id)
      if (idx >= 0 && b.group_id === groupId) {
        this.$set(this.series, idx, {...this.series[idx], ...b})
      } else if (groupId && (idx >= 0 || b.group_id === groupId)) {
        this.loadSeries()
      }
    },

    async loadSeries() {
      const groupId = this.battle?.group_id
      if (!groupId) {
        this.series = []
        return
      }
      try {
        const res = await api.mlbbEsports.groupBattles(groupId, {skipErrorToast: true})
        if (groupId !== this.battle?.group_id) return
        this.series = res?.data?.data || []
      } catch (e) {
        // серия — подсказка, без неё раздел работает
      }
    },

    /* ---------- эфир ---------- */

    patchPreview(patch) {
      this.air.preview = {...this.air.preview, ...patch}
    },

    async take() {
      if (this.taking) return
      const prev = this.air.program
      this.air.program = {...this.air.preview}
      if (!(await this.pushAir(this.air.program))) this.air.program = prev
    },

    async pushAir(program, battle = this.battle) {
      if (!this.broadcastId) {
        this.$bvToast.toast('Выберите или создайте трансляцию сверху — эфир не к чему привязать.', {
          title: 'Эфир не отправлен', variant: 'danger', solid: true,
        })
        return false
      }
      this.taking = true
      try {
        const body = programPayload({battleId: battle?.id || this.sourceBattleId, ...program})
        const res = await api.mlbbEsports.updateBroadcast(this.broadcastId, body)
        this.taking = false
        this.applyBroadcast(res?.data?.data)
        return true
      } catch (e) {
        // тост покажет интерсептор
        return false
      } finally {
        this.taking = false
      }
    },

    /* ---------- трансляция ---------- */

    broadcastLabel(b) {
      const parts = [`#${b.id}`]
      if (b.title) parts.push(b.title)
      if (b.battle) parts.push(battleTitle(b.battle))
      if (b.scene && b.scene !== 'none') parts.push(`в эфире: ${sceneTitle(b.scene)}`)
      return parts.join(' · ')
    },

    async loadBroadcasts() {
      this.broadcastsLoading = true
      try {
        const res = await api.mlbbEsports.broadcasts({}, {skipErrorToast: true})
        this.broadcasts = res?.data?.data || []
        // без выбора — самая свежая трансляция
        if (!this.broadcastId && this.broadcasts.length) this.selectBroadcast(this.broadcasts[0].id)
      } catch (e) {
        this.broadcasts = []
      } finally {
        this.broadcastsLoading = false
      }
    },

    selectBroadcast(id) {
      if (!id || id === this.broadcastId) return
      this.broadcastId = id
      try {
        window.localStorage.setItem(BROADCAST_STORAGE_KEY, String(id))
      } catch (e) {
        // без localStorage выбор живёт в ссылке
      }
      this.syncQuery()
    },

    async createBroadcast() {
      if (this.creatingBroadcast) return
      this.creatingBroadcast = true
      try {
        const res = await api.mlbbEsports.storeBroadcast({})
        const created = res?.data?.data
        if (created?.id) {
          this.broadcasts = [created, ...this.broadcasts.filter((b) => b.id !== created.id)]
          this.selectBroadcast(created.id)
        }
      } catch (e) {
        // тост покажет интерсептор
      } finally {
        this.creatingBroadcast = false
      }
    },

    startBroadcast(id) {
      this.stopBroadcast()
      this.broadcast = null
      this.broadcastMissing = false
      this.air.program = {scene: 'none', postId: null}
      if (!id) return
      this.broadcastLive = createBroadcastLive({
        broadcastId: id,
        onBroadcast: (raw) => {
          this.broadcastMissing = !raw
          this.applyBroadcast(raw)
        },
      })
      this.broadcastLive.start()
    },

    stopBroadcast() {
      if (this.broadcastLive) this.broadcastLive.stop()
      this.broadcastLive = null
      this.airElsewhere = null
    },

    applyBroadcast(raw) {
      if (raw && raw.id !== this.broadcastId) return
      this.broadcast = raw
      if (raw) {
        const idx = this.broadcasts.findIndex((b) => b.id === raw.id)
        if (idx >= 0) this.$set(this.broadcasts, idx, raw)
      }
      // пока ждём ответ на «В эфир», старое состояние с бэка не должно откатить кнопку
      if (this.taking) return
      const program = programOf(raw)
      // открыли трансляцию без выбранного матча — источником становится игра в эфире
      if (!this.sourceBattleId && program.battleId) {
        this.startLive(program.battleId)
        this.matchesOpen = false
        this.syncQuery()
      }
      if (program.battleId && program.battleId !== this.sourceBattleId) {
        this.airElsewhere = program.scene === 'none' ? null : program.battleId
        this.air.program = {scene: 'none', postId: null}
        return
      }
      this.airElsewhere = null
      this.air.program = {scene: program.scene, postId: program.postId}
    },

    async saveOverrides(patch) {
      if (!this.broadcastId || this.savingOverrides) return
      this.savingOverrides = true
      try {
        const res = await api.mlbbEsports.updateBroadcast(this.broadcastId, {overrides: patch})
        this.applyBroadcast(res?.data?.data)
        this.$bvToast.toast('Правки сохранены и уже в OBS', {title: 'Готово', variant: 'success', solid: true})
      } catch (e) {
        // тост покажет интерсептор (422 — неизвестный герой, кривой URL)
      } finally {
        this.savingOverrides = false
      }
    },

    async loadPostBattle(id) {
      if (!id || id === this.battle?.id || this.postBattleCache[id]) return
      try {
        const res = await api.mlbbEsports.battle(id, {skipErrorToast: true})
        const b = res?.data?.data
        if (b) this.$set(this.postBattleCache, id, b)
      } catch (e) {
        // итоги покажут заглушку «выберите игру»
      }
    },

    /* ---------- действия ---------- */

    async reconnect() {
      if (!this.battle) return
      this.reconnecting = true
      try {
        // опрос Moonton остановлен или упал — просим бэк включить его и дёрнуть один раз
        if (this.battle.poll_enabled === false || this.battle.last_sync_error) {
          await api.mlbbEsports.resyncBattle(this.battle.id)
          this.matchesReloadKey += 1
        }
        reconnectWs()
        await this.live.refresh()
      } catch (e) {
        // тост покажет интерсептор
      } finally {
        this.reconnecting = false
      }
    },

    openLink(b) {
      this.linkBattle = b
      this.linkVisible = true
    },

    onLinked() {
      this.matchesReloadKey += 1
      this.onBattleChanged(this.linkBattle?.id)
    },

    onBattleChanged(battleId) {
      // после link/unlink/resync бэк пришлёт updated по WS, но служебные поля опроса — только по REST
      if (battleId && battleId === this.sourceBattleId && this.live) this.live.refresh()
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";
</style>

<style lang="scss">
@import "~@/assets/scss/mlbb-broadcast.scss";

.mb-source {
  display: flex;
  flex-direction: column;

  &__main, &__series, &__unlinked {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 12px 18px;
    font-size: 13px;
  }

  &__series, &__unlinked { border-top: 1px solid var(--tl-border); }
  &__unlinked { border-radius: 0 0 var(--tl-radius) var(--tl-radius); }
  &__title { max-width: 440px; }
  &__sep { color: var(--tl-border); }

  .mb-chip:disabled { cursor: default; }
}

.mb-cast {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 18px;

  &__select { min-width: 260px; max-width: 520px; flex: 0 1 auto; }
}

.mb-matches-toggle {
  display: flex;
  justify-content: flex-end;

  .fa { margin-right: 4px; }
}
</style>
